import bigdecimalUtil from '../../../../../../lib/util/bigdecimal';
import { PLAN_TYPE } from '../../../../../../lib/document/schema/premium';

import Schema from '../Schema';

import i18nUtil from '../../util/i18n';

import { WELCOMING_GIFT } from '../../constants/shop';

const SALES_METHOD = {
  NORMAL: 'normal',
  BTO: 'bto',
  CONSIGNMENT: 'consignment',
  PRE_ORDER: 'pre_order'
};

const SALES_METHOD_PATTERN = [
  SALES_METHOD.NORMAL,
  SALES_METHOD.BTO,
  SALES_METHOD.CONSIGNMENT,
  SALES_METHOD.PRE_ORDER
];

const DISPLAY_SMALL_STOCK_COUNT = 10;

// Product Sku
const ProductSku = new Schema({
  _id: String,
  // SKU タイトル
  titles: {
    en_us: {
      type: String,
      default: ''
    },
    ja_jp: {
      type: String,
      default: ''
    }
  },
  // 短い SKU タイトル
  short_titles: {
    en_us: {
      type: String,
      default: ''
    },
    ja_jp: {
      type: String,
      default: ''
    }
  },

  // 価格
  // ref: ProductSkus.statics.getPrices
  prices: {
    general: {
      price: {
        type: Number,
        default: 0
      },
      sale_price: {
        type: Number,
        default: 0
      }
    },
    premium: {
      price: {
        type: Number,
        default: 0
      }
    }
  },

  sales_method: {
    type: String,
    enum: SALES_METHOD_PATTERN,
    default: SALES_METHOD.NORMAL
  },
  image: {
    source: {
      type: String,
      default: ''
    },
    width: Number,
    height: Number
  },

  expected_ship_at: Date,
  format_expected_ship_at_or_extra_week: String,

  // Premium会員価格を持っているかどうか
  has_premium_price: {
    type: Boolean,
    default: false
  },

  // 在庫数
  // FIXME: /shop/check_stock から移設
  stock_count: Number,

  // 在庫切れかどうか
  is_out_of_stock: {
    type: Boolean,
    default: false
  },
  // 配送可能国ではないかどうか
  is_denied_country: {
    type: Boolean,
    default: false
  },
  // 廃盤かどうか
  is_discontinued: {
    type: Boolean,
    default: false
  },

  // Free Shipping かどうか
  is_free_shipping: {
    type: Boolean,
    default: false
  },
  // 初回購入者限定 Free Shipping かどうか
  is_free_shipping_for_new_customer: {
    type: Boolean,
    default: false
  },
  // Premium 会員限定 Free Shipping かどうか
  is_free_shipping_for_premium_member: {
    type: Boolean,
    default: false
  },

  // デジタルコンテンツかどうか
  is_digital_content: {
    type: Boolean,
    default: false
  },

  // シリアルコードSKUかどうか
  is_serial_code: {
    type: Boolean,
    default: false
  },

  // ポイント交換対象のSKUかどうか
  is_points_redemption: {
    type: Boolean,
    default: false
  },

  // Premium会員限定のSKUかどうか
  is_premium_member_only: {
    type: Boolean,
    default: false
  },

  // 現地通貨価格
  local_currency_code: String,
  local_currency_price: Number,
  local_currency_premium_price: Number,

  // Wish list(Favorites) に追加済みかどうか
  is_added_to_wishlist: {
    type: Boolean,
    default: false
  },
  // Wish list に登録しているユーザー数
  wishlist_items_count: {
    type: Number,
    default: 0
  },
  // 再入荷通知に登録しているかどうか
  is_notify_registered: {
    type: Boolean,
    default: false
  },
  // 再入荷通知を登録しているユーザー数
  restock_subscribers_count: {
    type: Number,
    default: 0
  },

  // 通常獲得 TOM Point
  regular_acquire_point: {
    type: Number,
    default: 0
  },
  // 獲得 TOM Point 総額
  total_acquire_point: {
    type: Number,
    default: 0
  },
  // Premium 会員向け獲得 TOM Point
  total_acquire_point_for_premium: {
    type: Number,
    default: 0
  },
  // 獲得 TOM Point 率
  point_percentage: {
    type: Number,
    default: 0
  },
  // 獲得 Premium TOM Point 率
  premium_point_percentage: {
    type: Number,
    default: 0
  },
  // Premium Point Rate
  premium_point_rate: {
    type: Number,
    default: 0
  },
  // ボーナス TOM Point
  bonus_point: {
    type: Number,
    default: 0
  },

  // 配送可能な国についてworldwideに許可しているか
  is_allowed_for_worldwide_delivery: {
    type: Boolean,
    default: false
  },
  // UIでのcountriesの選択がallow、denyどちらだったか
  is_countries_shipping_allowed_on_ui: {
    type: Boolean,
    default: false
  },

  // 配送可能な国コード
  shipping_allowed_country_names: {
    type: Array,
    default: []
  },
  // 配送不能な国コード
  // @deprecated
  shipped_denied_country_names: {
    type: Array,
    default: []
  },
  // 配送不能な国の{国コード: 国名}のマップ
  shipped_denied_country_map: {
    type: Object,
    default: {}
  },

  // 倉庫リージョン
  region: {
    type: String,
    default: ''
  },

  // 配送カテゴリ
  shipping_categories: Array,

  // 最後の購入日時
  last_purchased_at: {
    type: Date
  },

  // オーディオコンテンツ
  audio_content: {
    _id: String,
    is_purchased: Boolean
  },
});

ProductSku.SALES_METHOD = SALES_METHOD;

// SKU が Sale 中かどうか
ProductSku.isSale = function (sku) {
  const salePrice =
    sku.prices && sku.prices.general && sku.prices.general.sale_price;
  if (!salePrice) {
    return false;
  }
  return typeof salePrice === 'number' && salePrice > 0;
};

// SKU の sale_price からの割引率を取得する
ProductSku.getDiscountRateFromSalePrice = function (sku, user) {
  if (sku && sku.prices && sku.prices.general) {
    if (user) {
      // userを受け取っている場合は、NonPremiumPriceから計算し返却する
      const price = ProductSku.getNonPremiumPrice(sku, user);
      return Math.round((1 - price / sku.prices.general.price) * 100);
    } else {
      // userを受け取っていない場合は、sale_priceから計算し返却する
      return Math.round(
        (1 - sku.prices.general.sale_price / sku.prices.general.price) * 100
      );
    }
  }
  return 0;
};

// SKU の Premium 価格からの割引率を取得する
ProductSku.getDiscountRateFromPremiumPrice = function (sku, user) {
  if (sku && sku.prices && sku.prices.premium && sku.prices.general) {
    const price = ProductSku.getPremiumPrice(sku, user);

    return Math.round((1 - price / sku.prices.general.price) * 100);
  }
  return 0;
};

// SKU 売り切れ or 在庫数がないかどうか
ProductSku.isOutOfStock = (sku) => sku.is_out_of_stock || !sku.stock_count;

// SKU ストック数を表示するかどうか
ProductSku.isDisplayStockCount = (sku) =>
  sku.sales_method !== SALES_METHOD.BTO &&
  sku.stock_count > 0 &&
  sku.stock_count <= DISPLAY_SMALL_STOCK_COUNT;

// SKU の定価よりも安く購入できる場合に表示する通常価格を取得する
ProductSku.getRegularPrice = function (sku, user) {
  if (!sku || !user) {
    return 0;
  }
  // Premium 会員価格が設定されていて、既存 Premium 会員または入会しようとしているユーザーの場合、通常価格を返却する
  if (
    sku.has_premium_price &&
    (user.is_premium_member || user.will_join_premium)
  ) {
    return sku.prices.general.price;
  } else if (ProductSku.isSale(sku)) {
    // セール価格がある場合、通常価格を返却する
    return sku.prices.general.price;
  } else if (
    sku.has_premium_price &&
    !user.is_login &&
    user.is_cart_empty &&
    !sku.is_points_redemption
  ) {
    // Premium 会員価格が設定されていて、非ログインユーザ、かつカートが空、かつポイント交換ではない場合、通常価格を返却する
    return sku.prices.general.price;
  }
  return 0;
};

// Welcomeクーポン適用後の価格を取得する
ProductSku.getNonPremiumPrice = function (sku, user) {
  let nonPremiumPrice = sku.prices.general.price;

  if (ProductSku.isSale(sku)) {
    nonPremiumPrice = sku.prices.general.sale_price;
  }
  // Premium 会員価格が設定されていて、非ログインユーザ、かつカートが空、かつポイント交換ではない場合Welcomeクーポン適用後の料金を返却する
  if (
    sku.has_premium_price &&
    !user.is_login &&
    user.is_cart_empty &&
    !sku.is_points_redemption
  ) {
    const price = bigdecimalUtil.subtract(
      nonPremiumPrice,
      WELCOMING_GIFT.WELCOMING_GIFT_AMOUNT_OFF
    );
    // 減算後の価格が$0以下になった場合は$0を返す
    nonPremiumPrice = Math.max(price, 0);
  }

  return nonPremiumPrice;
};

// SKU の実際に購入する販売金額を取得する
ProductSku.getSellingPrice = function (sku, user) {
  if (!sku || !user) {
    return 0;
  }

  let sellingPrice = sku.prices.general.price;

  // Premium 会員価格が設定されていて、既存 Premium 会員または入会しようとしているユーザーの場合、Premium 会員価格を返却する
  if (
    sku.has_premium_price &&
    (user.is_premium_member || user.will_join_premium)
  ) {
    return ProductSku.getPremiumPrice(sku, user);
    // セール価格がある場合、セール価格を返却する
  } else if (ProductSku.isSale(sku)) {
    sellingPrice = sku.prices.general.sale_price;
  }

  // Premium 会員価格が設定されていて、非ログインユーザ、かつカートが空、かつポイント交換ではない場合Welcomeクーポン適用後の料金を返却する
  if (
    sku.has_premium_price &&
    !user.is_login &&
    user.is_cart_empty &&
    !sku.is_points_redemption
  ) {
    const price = bigdecimalUtil.subtract(
      sellingPrice,
      WELCOMING_GIFT.WELCOMING_GIFT_AMOUNT_OFF
    );
    // 減算後の価格が$0以下になった場合は$0を返す
    sellingPrice = Math.max(price, 0);
  }

  return sellingPrice;
};

ProductSku.getPremiumPrice = (sku, user) => {
  if (!(sku && sku.has_premium_price)) {
    return 0;
  }

  // SKU 当たりの ShoppingCarts.getPremiumDiscountablePriceTotal
  const premiumDiscountablePrice = sku.prices.premium.price;
  // plan 入会費
  let premiumPlanPrice = 0;
  // premium 割引
  let premiumDiscount = 0;

  // 下記条件に一致する場合は入会ポイント分減算したP会員価格を表示
  // 1. 配送可能国である
  // 2. 販売中である
  // 3. カートが空である
  // 4. 入会ポイントが設定されている
  // 5. 初回もしくは再入会である
  // 6. Premium加入金額よりPremium商品価格のほうが高い
  if (
    !sku.is_denied_country &&
    !sku.is_out_of_stock &&
    user &&
    user.is_cart_empty &&
    user.possible_premium_plan &&
    user.possible_premium_plan.point &&
    user.possible_premium_plan_type &&
    (user.possible_premium_plan_type === PLAN_TYPE.FIRST ||
      user.possible_premium_plan_type === PLAN_TYPE.RECONTRACT) &&
    sku.prices.premium.price >= user.possible_premium_plan.price.value
  ) {
    premiumPlanPrice = user.possible_premium_plan.price.value;

    let premiumPoint = user.possible_premium_plan.point;
    if (
      user.possible_premium_plan_type === PLAN_TYPE.RECONTRACT &&
      user.can_gain_recontract_extra_point &&
      user.remain_subtotal_for_recontract_extra_point > 0 &&
      sku.prices.premium.price >=
        user.remain_subtotal_for_recontract_extra_point
    ) {
      premiumPoint = bigdecimalUtil.add(
        premiumPoint,
        user.recontract_extra_point
      );
    }
    premiumDiscount = bigdecimalUtil.divide(premiumPoint, 100).toFixed(2);
  }

  const premiumPrice = bigdecimalUtil.add(
    premiumDiscountablePrice,
    premiumPlanPrice
  );
  let skuPrice = bigdecimalUtil.subtract(
    premiumPrice,
    Math.min(premiumDiscount, premiumDiscountablePrice)
  );

  // 非ログインユーザもしくは Welcome クーポン未使用ユーザー、
  // かつカートが空、かつポイント交換ではない場合 Welcome クーポン適用後の料金を返却する
  // Welcome クーポンは premiumDiscountablePrice には影響しないので最後に引く
  if (
    (!user.is_login || user.has_welcoming_gift_coupon) &&
    user.is_cart_empty &&
    !sku.is_points_redemption
  ) {
    skuPrice = bigdecimalUtil.subtract(
      skuPrice,
      WELCOMING_GIFT.WELCOMING_GIFT_AMOUNT_OFF
    );
  }

  return Math.max(skuPrice, 0);
};

// SKU の割引率を取得する
ProductSku.getDiscountRate = function (sku, user) {
  if (!sku || !user) {
    return 0;
  }
  // Premium 会員価格が設定されていて、既存 Premium 会員または入会しようとしているユーザーの場合、Premium 会員価格を返却する
  if (
    sku.has_premium_price &&
    (user.is_premium_member || user.will_join_premium)
  ) {
    return ProductSku.getDiscountRateFromPremiumPrice(sku, user);
    // セール価格がある場合 or 非ログインユーザ、かつカートが空の場合、セール価格を返却する
  } else if (ProductSku.isSale(sku) || (!user.is_login && user.is_cart_empty)) {
    return ProductSku.getDiscountRateFromSalePrice(sku, user);
  }
  return 0;
};

// SKU の獲得できるTOM Pointsを取得する
ProductSku.getAcquirePoint = function (sku, user) {
  if (!sku || !user) {
    return 0;
  }
  if (
    (user.is_premium_member || user.will_join_premium) &&
    sku.total_acquire_point_for_premium
  ) {
    return sku.total_acquire_point_for_premium;
  } else {
    return sku.total_acquire_point;
  }
};

// SKU の獲得できるTOM Pointsのキャッシュバック率を取得する
ProductSku.getPointPercentage = function (sku, user) {
  if (!sku) {
    return 0;
  }
  if (
    user &&
    (user.is_premium_member || user.will_join_premium) &&
    sku.premium_point_percentage
  ) {
    return sku.premium_point_percentage;
  } else {
    return sku.point_percentage;
  }
};

// 年間プラン切り替え購入時の通常 Premium SKU Price
ProductSku.getAnnualPremiumSkuPrice = (sku, user) => {
  if (!sku || !user) {
    return 0;
  }

  let skuPrice = sku.prices.general.price;

  if (
    sku.has_premium_price &&
    (user.is_premium_member || user.will_join_premium)
  ) {
    skuPrice = sku.prices.premium.price;
  } else if (ProductSku.isSale(sku)) {
    skuPrice = sku.prices.general.sale_price;
  }

  if (
    (!user.is_login || user.has_welcoming_gift_coupon) &&
    user.is_cart_empty &&
    !sku.is_points_redemption
  ) {
    skuPrice = bigdecimalUtil.subtract(
      skuPrice,
      WELCOMING_GIFT.WELCOMING_GIFT_AMOUNT_OFF
    );
  }

  return Math.max(skuPrice, 0);
};

// 年間プラン切り替え購入時の年間割引込みの Premium SKU Price
ProductSku.getAnnualPremiumSkuDiscountdPrice = (sku, user) => {
  if (!sku || !user) {
    return 0;
  }

  let premiumPrice = ProductSku.getAnnualPremiumSkuPrice(sku, user);

  if (
    !sku.is_denied_country &&
    !sku.is_out_of_stock &&
    user &&
    user.is_cart_empty &&
    user.possible_premium_plan &&
    user.possible_premium_plan.point &&
    user.possible_premium_plan_type &&
    user.can_buy_with_annual_plan
  ) {
    let discountedPrice = premiumPrice;
    discountedPrice = bigdecimalUtil.add(
      discountedPrice,
      user.possible_premium_plan.price.value
    );

    const premiumPoint = user.possible_premium_plan.point;
    const premiumDiscount = bigdecimalUtil.divide(premiumPoint, 100).toFixed(2);
    discountedPrice = bigdecimalUtil.subtract(discountedPrice, premiumDiscount);
    if (discountedPrice >= 0.0) {
      premiumPrice = discountedPrice;
    }
  }

  return premiumPrice;
};

// getAnnualPremiumSkuPrice の discount rate
ProductSku.getAnnualPremiumSkuPriceRate = function (sku, user) {
  if (!sku || !user) {
    return 0;
  }
  if (sku && sku.prices && sku.prices.general) {
    const price = ProductSku.getAnnualPremiumSkuPrice(sku, user);
    return Math.round((1 - price / sku.prices.general.price) * 100);
  }
  return 0;
};

// getAnnualPremiumSkuDiscountdPrice の discount rate
ProductSku.getAnnualPremiumSkuDiscountRate = function (sku, user) {
  if (!sku || !user) {
    return 0;
  }
  if (sku && sku.prices && sku.prices.general) {
    const price = ProductSku.getAnnualPremiumSkuDiscountdPrice(sku, user);
    return Math.round((1 - price / sku.prices.general.price) * 100);
  }
  return 0;
};

// Annual Plan 選択後に実質的な割引が発生する場合 true
ProductSku.hasEffectiveAnnualDiscount = function (sku, user) {
  if (!user.is_cart_empty) {
    return false;
  }
  const skuPrice = ProductSku.getAnnualPremiumSkuPrice(sku, user);
  if (skuPrice <= 0) {
    return false;
  }

  const premiumPoint = user.possible_premium_plan.point;
  const premiumDiscount = bigdecimalUtil.divide(premiumPoint, 100).toFixed(2);
  return skuPrice >= premiumDiscount;
};

ProductSku.isBto = (sku) => sku.sales_method === SALES_METHOD.BTO;

ProductSku.isPreOrder = (sku) => sku.sales_method === SALES_METHOD.PRE_ORDER;

// 配送予定日のフォーマットを正しく返す
const WRONG_EXTRA_WEEK = '2 - 3 weeks';
const EXACT_EXTRA_WEEK = '2 or 3 weeks';
ProductSku.getExpectedShipAtOrExtraWeek = (sku) =>
  (sku.format_expected_ship_at_or_extra_week || WRONG_EXTRA_WEEK)
    .replace('weeks', i18nUtil.__('weeks'))
    .replace('week', i18nUtil.__('week'))
    .replace(WRONG_EXTRA_WEEK, EXACT_EXTRA_WEEK);

/**
 * fullTitle (titles と short_titles を結合した文字列) を取得する
 *   en_us 優先して取得して、無ければ ja_jp を取得する
 **/
ProductSku.fullTitleEnPreferred = (sku) => {
  const chooseLanguageTitle = (titles, language) => {
    var title = '';
    if (titles && titles[language]) {
      title = titles[language];
    }
    return title;
  };

  let fullTitle = '';

  // en_us 優先
  fullTitle = chooseLanguageTitle(sku.titles, 'en_us');
  if (fullTitle) {
    fullTitle += ` ${chooseLanguageTitle(sku.short_titles, 'en_us')}`;
    return fullTitle;
  }

  return fullTitle;
};

ProductSku.isUndeliverableSku = (sku) => {
  return sku.undeliverable;
};

ProductSku.isPreOrderSku = (sku) => {
  return sku.sales_method === SALES_METHOD.PRE_ORDER;
};

ProductSku.isBtoSku = (sku) => {
  return sku.sales_method === SALES_METHOD.BTO;
};

/**
 * TOMバルク船便候補商品かつ在庫ある商品の場合
 */
ProductSku.isCanBulkShippingAndStock = (sku, access_country_code) => {
  // ref: lib/util/shipping_method.js
  const bulk_shipping_categories = [
    '52b0235be763d6da64000033', // figure (No Adhesive)
    '52b95e093e6fcec062000030', // book
    '52468efef01386914800003e', // Plastic Model
    '60627541575be828cdf36bef', // toy figure
    '52148256052691be75000078' // Plushie
  ];
  return (
    access_country_code === 'US' &&
    sku.region === 'Maihama' &&
    sku.shipping_categories.some((c) =>
      bulk_shipping_categories.includes(c._id)
    ) &&
    !sku.is_out_of_stock &&
    sku.stock_count
  );
};

ProductSku.isBeforeExpectedShipAt = (sku) => {
  if (!sku.expected_ship_at) {
    return false;
  }
  const expectedShipAt = new Date(sku.expected_ship_at);
  return (
    new Date() <
    new Date(expectedShipAt.getFullYear(), expectedShipAt.getMonth(), 1)
  );
};

// 在庫あり商品のリードタイム
ProductSku.inStockExpectedShipDays = (sku) => {
  if (sku && sku.region === 'Commerce') {
    return '2-4';
  }
  return '7-10';
};

export default ProductSku;
